import { useCallback } from "react";
import { ServerMessages, useSocketContext } from "../../context/SocketContext";
import { Alert, ConfirmAlertResponse, CreateAlertPayload, ValidateAlertExistsResponse } from "../../types/Alert";
import { getReqUrl } from "../../config/reqUrlConfig";

const useClientAlertsService = () => {
    const { emit } = useSocketContext();
    const reqUrl = getReqUrl();

    const fetchAllAlerts = useCallback(
        async (
            activeOnly = true,
            sortReverse = true,
        ): Promise<Alert[]> => {
            const response = await emit<Alert[]>({
                message: ServerMessages.getAllAlerts,
                activeOnly,
                sortReverse,
            });

            return response?.data ?? [];
        },
        [emit],
    );

    const createAlert = useCallback(async (createAlertPayload: CreateAlertPayload): Promise<Alert> => {
        const response = await emit<Alert>({
            message: ServerMessages.createAlert,
            alert: createAlertPayload,
        });

        if (!response || response.data == null) {
            throw new Error('Error: no data returned from server');
        }

        return response.data;
    }, [emit]);

    const updateAlert = useCallback(async (alert: Partial<Alert>): Promise<Alert> => {
        const response = await emit<Alert>({
            message: ServerMessages.updateAlert,
            alert,
        });

        if (!response || response.data == null) {
            throw new Error('Error: no data returned from server');
        }

        return response.data;
    }, [emit]);

    const deleteAlert = useCallback(async (alertId: number): Promise<void> => {
        await emit({
            message: ServerMessages.deleteAlert,
            alertId,
        });
    },
        [emit]);

    const validateAlertExists = useCallback(
        async (confirmationId: string): Promise<ValidateAlertExistsResponse> => {
            const response = await fetch(`${reqUrl}/api/alertConfirmationExists`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ confirmationId }),
            });

            return response.json();
        }, [reqUrl],
    );

    const confirmAlert = useCallback(
        async (confirmationId: string): Promise<ConfirmAlertResponse> => {
            const response = await fetch(`${reqUrl}/api/confirmAlertExecuted`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ confirmationId }),
            });

            return response.json();
        }, [reqUrl],
    );

    return {
        fetchAllAlerts,
        createAlert,
        updateAlert,
        deleteAlert,
        validateAlertExists,
        confirmAlert,
    };
};

export default useClientAlertsService;
